import { Tooltip, TooltipProps } from "flowbite-react";
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeToggler";

type TTooltipCompProps = {
  children?: ReactNode;
  content?: ReactNode | string;
} & Omit<TooltipProps, "content">;

const TooltipComp = forwardRef<HTMLDivElement, TTooltipCompProps>(
  ({ children, content, ...props }, ref) => {
    const { theme } = useTheme();

    // Convert string content with \n to JSX with <br/> tags
    const formatContent = (content: ReactNode | string) => {
      if (typeof content === "string") {
        return content.split("\n").map((line, index) => (
          <span key={index} className="leading-5">
            {line}
            {index < content.split("\n").length - 1 && <br />}
          </span>
        ));
      }
      return content;
    };

    return (
      <Tooltip
        content={formatContent(content)}
        {...props}
        style={theme === "dark" ? "light" : "dark"}
        className={`min-w-max max-w-32 whitespace-normal text-xs leading-tight z-50 ${props.className ?? ""}`}
        placement={props.placement ?? "right"}
        trigger={props?.trigger ?? "hover"}
        animation="duration-300"
        arrow={true}
      >
        <div ref={ref}>{children}</div>
      </Tooltip>
    );
  },
);
TooltipComp.displayName = "TooltipComp";

type TPortalTooltipPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end";

type TPortalTooltipCompProps = {
  children?: ReactNode;
  className?: string;
  content?: ReactNode | string;
  placement?: TPortalTooltipPlacement;
  trigger?: "hover" | "click";
};

const PortalTooltipComp = ({
  children,
  className,
  content,
  placement = "right",
  trigger = "hover",
}: TPortalTooltipCompProps) => {
  const { theme } = useTheme();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<CSSProperties>({});
  const [activeSide, setActiveSide] = useState<string>(placement.split("-")[0]);

  const isLightTooltip = theme === "dark";
  const tooltipThemeClass = isLightTooltip
    ? "bg-white text-gray-900 border border-gray-200"
    : "bg-gray-900 text-white";

  const formatContent = (content: ReactNode | string) => {
    if (typeof content === "string") {
      return content.split("\n").map((line, index) => (
        <span key={index} className="leading-5">
          {line}
          {index < content.split("\n").length - 1 && <br />}
        </span>
      ));
    }
    return content;
  };

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    const gap = 8;
    const tooltipWidth = 320;
    const tooltipHeight = 280;

    let side = placement.split("-")[0] as "top" | "right" | "bottom" | "left";

    // AUTO FLIP
    if (side === "right" && rect.right + tooltipWidth > window.innerWidth) {
      side = "left";
    }

    if (side === "left" && rect.left - tooltipWidth < 0) {
      side = "right";
    }

    if (side === "bottom" && rect.bottom + tooltipHeight > window.innerHeight) {
      side = "top";
    }

    if (side === "top" && rect.top - tooltipHeight < 0) {
      side = "bottom";
    }

    let top = 0;
    let left = 0;
    let transform = "";

    switch (side) {
      case "top":
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        transform = "translate(-50%, -100%)";
        break;

      case "bottom":
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        transform = "translateX(-50%)";
        break;

      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        transform = "translate(-100%, -50%)";
        break;

      default:
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        transform = "translateY(-50%)";
    }

    setTooltipPosition({
      top: Math.max(10, top),
      left: Math.max(10, left),
      transform,
    });
    setActiveSide(side);
  }, [placement]);

  useEffect(() => {
    if (!isVisible) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, updatePosition]);

  const showTooltip = () => {
    updatePosition();
    setIsVisible(true);
  };

  const hideTooltip = () => {
    if (trigger === "hover") setIsVisible(false);
  };

  const toggleTooltip = () => {
    if (trigger === "click") setIsVisible((current) => !current);
  };

  const arrowColorClass = isLightTooltip ? "white" : "gray-900";
  const arrowClass =
    activeSide === "top"
      ? `top-full left-1/2 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent ${arrowColorClass === "white" ? "border-t-white" : "border-t-gray-900"}`
      : activeSide === "bottom"
        ? `bottom-full left-1/2 -translate-x-1/2 border-x-4 border-b-4 border-x-transparent ${arrowColorClass === "white" ? "border-b-white" : "border-b-gray-900"}`
        : activeSide === "left"
          ? `left-full top-1/2 -translate-y-1/2 border-y-4 border-l-4 border-y-transparent ${arrowColorClass === "white" ? "border-l-white" : "border-l-gray-900"}`
          : `right-full top-1/2 -translate-y-1/2 border-y-4 border-r-4 border-y-transparent ${arrowColorClass === "white" ? "border-r-white" : "border-r-gray-900"}`;

  return (
    <>
      <div
        ref={triggerRef}
        className="w-fit max-w-full"
        onMouseEnter={trigger === "hover" ? showTooltip : undefined}
        onMouseLeave={hideTooltip}
        onFocus={trigger === "hover" ? showTooltip : undefined}
        onBlur={hideTooltip}
        onClick={toggleTooltip}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            className={` fixed z-[9999] min-w-fit  max-w-[min(320px,calc(100vw-20px))] max-h-[280px] whitespace-normal break-words rounded-lg  px-3 py-2 text-xs leading-tight  shadow-lg pointer-events-auto ${tooltipThemeClass} ${className ?? ""}`}
            style={tooltipPosition}
            role="tooltip"
          >
            {formatContent(content)}
            <div
              data-testid="tooltip-arrow"
              className={`absolute h-0 w-0 ${arrowClass}`}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export { PortalTooltipComp, TooltipComp };
